import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Bell, Inbox } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { cn } from '../lib/utils';
import { useErrorEventNotifications } from '../hooks/useErrorEventNotifications';
import { SEVERITY_COLORS, SEVERITY_LABELS } from './pages/error-events/badges';
import { ErrorEventSeverity } from '../services/api';

/** Severities absteigend nach Dringlichkeit — Reihenfolge der Aufschlüsselung im Dropdown. */
const SEVERITY_ORDER: ErrorEventSeverity[] = ['critical', 'very_high', 'high', 'medium', 'low'];

/** Wie viele Severity-Badges das Dropdown maximal zeigt — mehr macht es unruhig. */
const MAX_SEVERITY_BADGES = 2;

/**
 * Benachrichtigungs-Glocke der Kopfzeile: pollt offene Error-Events + DLQ-Tiefe
 * (beim Laden + alle 30 s), zeigt einen Zähler-Badge und bietet den Opt-in für
 * native Desktop-Benachrichtigungen an.
 *
 * Nur für Super-Admins rendern — die Summary-API ist Super-Admin-only.
 */
export function NotificationBell() {
  const navigate = useNavigate();
  const {
    summary,
    lastCheckedAt,
    desktopEnabled,
    permission,
    enableDesktopNotifications,
    disableDesktopNotifications,
  } = useErrorEventNotifications({ enabled: true, onNavigate: navigate });

  const openTotal = summary?.openTotal ?? 0;
  const dlqTotal = summary?.dlqTotal ?? 0;
  const badgeTotal = openTotal + dlqTotal;

  const topSeverities = SEVERITY_ORDER.map((severity) => ({
    severity,
    count: summary?.openBySeverity[severity] ?? 0,
  }))
    .filter((entry) => entry.count > 0)
    .slice(0, MAX_SEVERITY_BADGES);

  const desktopActive = desktopEnabled && permission === 'granted';

  const handleDesktopToggle = (checked: boolean) => {
    if (checked) {
      void enableDesktopNotifications();
    } else {
      disableDesktopNotifications();
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="relative" aria-label="Benachrichtigungen">
          <Bell className="w-4 h-4" />
          {badgeTotal > 0 && (
            <span className="absolute -top-1 -right-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-semibold leading-none text-white ring-2 ring-white">
              {badgeTotal > 99 ? '99+' : badgeTotal}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-80" align="end">
        <DropdownMenuLabel>Benachrichtigungen</DropdownMenuLabel>
        <DropdownMenuSeparator />

        <DropdownMenuItem onClick={() => navigate('/error-events')} className="flex items-start gap-3 py-2">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-gray-500" />
          <div className="flex-1 space-y-1">
            <div className="flex items-center justify-between">
              <span className={openTotal > 0 ? 'font-medium' : 'text-muted-foreground'}>
                Offene Error-Events
              </span>
              <Badge className={openTotal > 0 ? 'bg-red-600 text-white' : 'bg-gray-100 text-gray-500'}>
                {openTotal}
              </Badge>
            </div>
            {topSeverities.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {topSeverities.map(({ severity, count }) => (
                  <Badge key={severity} className={cn('text-xs', SEVERITY_COLORS[severity])}>
                    {count}× {SEVERITY_LABELS[severity]}
                  </Badge>
                ))}
              </div>
            )}
          </div>
        </DropdownMenuItem>

        <DropdownMenuItem onClick={() => navigate('/error-events?tab=dlq')} className="flex items-start gap-3 py-2">
          <Inbox className="mt-0.5 h-4 w-4 shrink-0 text-gray-500" />
          <div className="flex flex-1 items-center justify-between">
            <span className={dlqTotal > 0 ? 'font-medium' : 'text-muted-foreground'}>Dead Letter Queue</span>
            {summary?.dlqTotal === null ? (
              <span className="text-xs text-muted-foreground">nicht erreichbar</span>
            ) : (
              <Badge className={dlqTotal > 0 ? 'bg-red-600 text-white' : 'bg-gray-100 text-gray-500'}>
                {dlqTotal}
              </Badge>
            )}
          </div>
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        {/* Kein DropdownMenuItem: der Schalter soll das Menü nicht schließen. */}
        <div className="space-y-1 px-2 py-2">
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm">Desktop-Benachrichtigungen</span>
            <Switch
              checked={desktopActive}
              onCheckedChange={handleDesktopToggle}
              disabled={permission === 'denied' || permission === 'unsupported'}
              aria-label="Desktop-Benachrichtigungen umschalten"
            />
          </div>
          {permission === 'denied' && (
            <p className="text-xs text-red-600">
              Im Browser blockiert — in den Seiteneinstellungen erlauben.
            </p>
          )}
          {permission === 'unsupported' && (
            <p className="text-xs text-muted-foreground">Dieser Browser unterstützt keine Benachrichtigungen.</p>
          )}
          {permission === 'default' && !desktopActive && (
            <p className="text-xs text-muted-foreground">Browser fragt nach Erlaubnis.</p>
          )}
        </div>

        <DropdownMenuSeparator />
        <div className="px-2 py-1.5 text-xs text-muted-foreground">
          {lastCheckedAt
            ? `Zuletzt geprüft: ${lastCheckedAt.toLocaleTimeString('de-DE')} · alle 30 s`
            : 'Noch nicht geprüft'}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
